import { useBlockNavigation } from '@app/src/hooks/useBlockNavigation'
import type { OrganizationRiskSetting } from '@app/src/types/resourceExplorer'
import type { Location } from 'history'
import { useMemo, useState } from 'react'
import { applyModifications } from './Cards/riskIndexCards'
import { useSaveRiskSettings } from './useSaveRiskSettings'

export interface RiskSettingsEditor {
  isEditMode: boolean
  /** Fetched settings with any pending (unsaved) activation edits overlaid. */
  effectiveSettings: OrganizationRiskSetting[]
  /** Pending activation edits (riskIndexId → desired state) — for consumers that render raw rows. */
  modifiedRiskStates: Map<number, boolean>
  hasChanges: boolean
  isSaving: boolean
  isBlocked: boolean
  startEdit: () => void
  cancelEdit: () => void
  toggleIndex: (riskIndexId: number, isActivated: boolean) => void
  save: (shouldNavigate?: boolean) => Promise<void>
  discardAndLeave: () => void
  stayOnPage: () => void
}

interface RiskSettingsEditorOptions {
  /** Lets a tabbed view keep the edit session alive while navigating between its own tabs. */
  shouldBlockLocation?: (nextLocation: Location) => boolean
}

/**
 * Owns the risk-index edit session: pending activation edits, save, and the unsaved-changes
 * navigation guard. Shared by both the legacy table and the topic cards so the state machine
 * lives in one place, and so each view stays a thin presentation shell.
 */
export const useRiskSettingsEditor = (
  riskSettings: OrganizationRiskSetting[],
  { shouldBlockLocation }: RiskSettingsEditorOptions = {},
): RiskSettingsEditor => {
  const { saveRiskSettings, isSaving } = useSaveRiskSettings()
  const [isEditMode, setIsEditMode] = useState(false)
  const [modifiedRiskStates, setModifiedRiskStates] = useState<Map<number, boolean>>(new Map())

  const hasChanges = modifiedRiskStates.size > 0
  const { isBlocked, confirmNavigation, cancelNavigation } = useBlockNavigation(hasChanges && isEditMode, {
    shouldBlockLocation,
  })

  const effectiveSettings = useMemo(
    () => applyModifications(riskSettings, modifiedRiskStates),
    [riskSettings, modifiedRiskStates],
  )

  const toggleIndex = (riskIndexId: number, isActivated: boolean) => {
    setModifiedRiskStates(previous => {
      const next = new Map(previous)
      const original = riskSettings.find(setting => setting.riskType.id === riskIndexId)

      if (original && original.isActivated === isActivated) next.delete(riskIndexId)
      else next.set(riskIndexId, isActivated)

      return next
    })
  }

  const resetEdit = () => {
    setModifiedRiskStates(new Map())
    setIsEditMode(false)
  }

  const save = async (shouldNavigate = false) => {
    const updates = Array.from(modifiedRiskStates.entries()).map(([riskIndexId, isActivated]) => ({
      riskIndexId,
      isActivated,
    }))

    const succeeded = await saveRiskSettings(updates)
    if (!succeeded) return

    resetEdit()
    if (shouldNavigate) confirmNavigation()
  }

  const discardAndLeave = () => {
    resetEdit()
    confirmNavigation()
  }

  return {
    isEditMode,
    effectiveSettings,
    modifiedRiskStates,
    hasChanges,
    isSaving,
    isBlocked,
    startEdit: () => setIsEditMode(true),
    cancelEdit: resetEdit,
    toggleIndex,
    save,
    discardAndLeave,
    stayOnPage: cancelNavigation,
  }
}
